<template>
  <div class="page-container">
    <PageHeader title="申请记录管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增</el-button>
      </template>
    </PageHeader>

    <!-- 搜索面板 -->
    <SearchPanel>
      <el-select v-model="query.rrmaCustomer" clearable placeholder="客户群体" size="small" @change="handleSearchCustomerChange">
        <el-option v-for="item in searchCustomerOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaProvince" clearable placeholder="省份" size="small" :disabled="!query.rrmaCustomer" @change="handleSearchProvinceChange">
        <el-option v-for="item in searchProvinceOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaCity" clearable placeholder="地市" size="small" :disabled="!query.rrmaProvince" @change="handleSearchCityChange">
        <el-option v-for="item in searchCityOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaCourseType" clearable placeholder="课程类型" size="small" :disabled="!query.rrmaCity" @change="handleSearch">
        <el-option v-for="item in searchCourseTypeOptions" :key="item" :label="item" :value="item"></el-option>
      </el-select>
      <el-select v-model="query.rrmaStatus" clearable placeholder="申请状态" size="small" @change="handleSearch">
        <el-option label="待提交" :value="1"></el-option>
        <el-option label="待审核" :value="2"></el-option>
        <el-option label="已发包" :value="3"></el-option>
      </el-select>
      <el-input v-model="query.applyusername" clearable placeholder="申请人" size="small" style="width: 130px" />
      <el-input v-model="query.checkusername" clearable placeholder="审核人" size="small" style="width: 130px" />
      <el-input v-model="query.keySearch" clearable placeholder="关键词搜索" size="small" style="width: 200px" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <div class="apply-status-tabs" v-loading="statusGroupLoading">
      <button
        v-for="item in statusTabs"
        :key="item.status"
        type="button"
        class="apply-status-tabs__item"
        :class="{ 'is-active': item.active }"
        @click="handleStatusTabClick(item)"
      >
        <span>{{ item.label }}</span>
        <strong>（{{ item.value }}）</strong>
      </button>
    </div>

    <!-- 列表 -->
    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="rrmaId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      >
        <template #cell-rrmaStatus="{ row }">
          <span :style="{ color: statusColorMap[row.rrmaStatus] || '#909399', fontWeight: '500' }">{{ statusMap[row.rrmaStatus] || '未知' }}</span>
        </template>
      </aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无申请记录" icon="el-icon-document-checked"></EmptyState>
    </TablePanel>

    <!-- 编辑弹窗 -->
    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="editFormConfig"
      :form-data="editFormData"
      dialog-title="编辑申请"
      dialog-width="80%"
      :confirm-loading="submitLoading"
      @confirm="handleEditSubmit"
      @item-change="handleFormItemChange('edit', $event)"
    >
      <template #footer="{ confirm, cancel }">
        <el-button size="small" @click="cancel">取消</el-button>
        <el-button size="small" :loading="submitLoading" @click="editFormData.rrmaStatus = 1; editSubmitType = 1; confirm()">保存</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="editFormData.rrmaStatus = 2; editSubmitType = 2; confirm()">提交</el-button>
      </template>
    </aj-form-edit>

    <!-- 新增弹窗 -->
    <aj-form-add
      :dialog-visible.sync="addDialogVisible"
      :form-config="addFormConfig"
      :form-data="addFormData"
      dialog-title="新增申请"
      dialog-width="80%"
      :confirm-loading="submitLoading"
      @confirm="handleAddConfirm"
      @item-change="handleFormItemChange('add', $event)"
    >
      <template #footer="{ confirm, cancel }">
        <el-button size="small" @click="cancel">取消</el-button>
        <el-button size="small" :loading="submitLoading" @click="addFormData.rrmaStatus = 1; addSubmitType = 1; confirm()">保存</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="addFormData.rrmaStatus = 2; addSubmitType = 2; confirm()">提交</el-button>
      </template>
    </aj-form-add>

    <!-- 审核弹窗（已合并到详情弹窗，此块保留占位） -->

    <!-- 查看详情弹窗（含审核操作） -->
    <el-dialog v-dialog-drag-resize :visible.sync="viewDialogVisible" width="80%" top="7vh" custom-class="detail-dialog" :close-on-click-modal="false" :show-close="false">
      <template #title>
        <div class="detail-dialog-header">
          <span class="detail-dialog-title">申请记录详情</span>
          <i class="dialog-close-btn" @click="viewDialogVisible = false" />
        </div>
      </template>
      <div class="detail-body" v-if="viewData">
        <!-- 可滚动的内容区 -->
        <div class="detail-scroll">
        <!-- 状态栏 -->
        <div class="detail-status-bar">
          <span class="detail-status-tag" :class="'detail-status--' + viewData.rrmaStatus">
            {{ statusMap[viewData.rrmaStatus] || '未知' }}
          </span>
        </div>

        <!-- 基本信息 -->
        <div class="detail-section-title">
          <i class="el-icon-office-building"></i>
          <span>基本信息</span>
        </div>
        <div class="detail-table detail-table--2col">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">销售订单号</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.rfiOrderNum || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">行程名称</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.rfiProjectName || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">客户群体</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaCustomer || '-' }}</div>
            <div class="detail-cell detail-cell--label">省份</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaProvince || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">地市</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaCity || '-' }}</div>
            <div class="detail-cell detail-cell--label">课程类型</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmaCourseType || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">包内含资料</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.rrmaModelName || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">客户名称</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameCustomer || '-' }}</div>
            <div class="detail-cell detail-cell--label">客户简称</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameCustomerShort || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">课程名称</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ viewData.applyRuleNameCourse || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">开始日期</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameDateStart || '-' }}</div>
            <div class="detail-cell detail-cell--label">结束日期</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyRuleNameDateEnd || '-' }}</div>
          </div>
        </div>

        <!-- 资料信息 -->
        <div class="detail-section-title">
          <i class="el-icon-folder-opened"></i>
          <span>资料信息</span>
        </div>
        <div class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">考勤成绩模板</div>
            <div class="detail-cell detail-cell--value">
              <div>{{ viewData.rtReportModel && viewData.rtReportModel.relationRrmfIdOneName || '-' }}</div>
              <template v-if="viewData.rtReportModel && viewData.rtReportModel.rtReportModelFileOne">
                <div v-if="viewData.rtReportModel.rtReportModelFileOne.rrmfDes" class="template-des">备注：{{ viewData.rtReportModel.rtReportModelFileOne.rrmfDes }}</div>
                <div v-if="viewData.rtReportModel.rtReportModelFileOne.rrmfImages" class="template-img-wrap">
                  <el-image :src="viewData.rtReportModel.rtReportModelFileOne.rrmfImages" fit="contain" :preview-src-list="[viewData.rtReportModel.rtReportModelFileOne.rrmfImages]" style="width: 160px; height: 100px; border-radius: 6px; border: 1px solid #e2e8f0;" />
                </div>
              </template>
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">评估分数模板</div>
            <div class="detail-cell detail-cell--value">
              <div>{{ viewData.rtReportModel && viewData.rtReportModel.relationRrmfIdTwoName || '-' }}</div>
              <template v-if="viewData.rtReportModel && viewData.rtReportModel.rtReportModelFileTwo">
                <div v-if="viewData.rtReportModel.rtReportModelFileTwo.rrmfDes" class="template-des">备注：{{ viewData.rtReportModel.rtReportModelFileTwo.rrmfDes }}</div>
                <div v-if="viewData.rtReportModel.rtReportModelFileTwo.rrmfImages" class="template-img-wrap">
                  <el-image :src="viewData.rtReportModel.rtReportModelFileTwo.rrmfImages" fit="contain" :preview-src-list="[viewData.rtReportModel.rtReportModelFileTwo.rrmfImages]" style="width: 160px; height: 100px; border-radius: 6px; border: 1px solid #e2e8f0;" />
                </div>
              </template>
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">总结报告模板</div>
            <div class="detail-cell detail-cell--value">
              <div>{{ viewData.rtReportModel && viewData.rtReportModel.relationRrmfIdThreeName || '-' }}</div>
              <template v-if="viewData.rtReportModel && viewData.rtReportModel.rtReportModelFileThree">
                <div v-if="viewData.rtReportModel.rtReportModelFileThree.rrmfDes" class="template-des">备注：{{ viewData.rtReportModel.rtReportModelFileThree.rrmfDes }}</div>
                <div v-if="viewData.rtReportModel.rtReportModelFileThree.rrmfImages" class="template-img-wrap">
                  <el-image :src="viewData.rtReportModel.rtReportModelFileThree.rrmfImages" fit="contain" :preview-src-list="[viewData.rtReportModel.rtReportModelFileThree.rrmfImages]" style="width: 160px; height: 100px; border-radius: 6px; border: 1px solid #e2e8f0;" />
                </div>
              </template>
            </div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">本地附加</div>
            <div class="detail-cell detail-cell--value detail-cell--pre">{{ (viewData.rtReportModel && viewData.rtReportModel.rrmAttachment) || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">材料特殊性备注</div>
            <div class="detail-cell detail-cell--value detail-cell--pre">{{ (viewData.rtReportModel && viewData.rtReportModel.rrmSpecial) || '-' }}</div>
          </div>
        </div>

        <!-- 审核信息 -->
        <div class="detail-section-title">
          <i class="el-icon-s-check"></i>
          <span>审核信息</span>
        </div>
        <div class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">申请人</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applyusername || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">申请时间</div>
            <div class="detail-cell detail-cell--value">{{ viewData.applytime || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">审核人</div>
            <div class="detail-cell detail-cell--value">{{ viewData.checkusername || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">审核时间</div>
            <div class="detail-cell detail-cell--value">{{ viewData.checktime || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">审核备注</div>
            <div class="detail-cell detail-cell--value detail-cell--pre">{{ viewData.rrmaStatusDes || '-' }}</div>
          </div>
        </div>

        <!-- 审核操作区已移至弹窗底部悬浮 -->

        <!-- 资料地址（已发包状态才展示） -->
        <template v-if="viewData.rrmaStatus === 3 && viewData.rrmaModelPath">
          <div class="detail-section-title">
            <i class="el-icon-folder-opened"></i>
            <span>资料下载</span>
          </div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-cell detail-cell--label">资料地址</div>
              <div class="detail-cell detail-cell--value">
                <div class="detail-file-list">
                  <span v-for="(url, idx) in viewData.rrmaModelPath.split(',').filter(Boolean)" :key="idx" class="detail-file-chip detail-file-preview" @click="openFilePreview(url)">
                    <i class="el-icon-view"></i>
                    <span>{{ viewData.rrmaModelName || url.split('/').pop() }}</span>
                  </span>
                  <el-button type="text" size="mini" icon="el-icon-download" class="detail-download-btn" @click="downloadToLocal(viewData.rrmaModelPath.split(',')[0])">保存到电脑</el-button>
                </div>
              </div>
            </div>
          </div>
        </template>
        </div><!-- /detail-scroll 结束 -->

        <!-- 审核操作区：固定在弹窗底部，不随内容滚动 -->
        <div v-if="viewData.rrmaStatus === 2" class="detail-check-bar">
          <span class="detail-check-bar__label"><i class="el-icon-edit"></i> 审核备注</span>
          <el-input v-model="checkForm.rrmaStatusDes" type="textarea" :rows="2" placeholder="选填审核备注" class="detail-check-bar__input" />
        </div>
      </div><!-- /detail-body 结束 -->
      <template #footer>
        <el-button size="small" @click="viewDialogVisible = false">关 闭</el-button>
        <el-button v-if="viewData && viewData.rrmaStatus === 2" type="primary" size="small" :loading="submitLoading" @click="handleCheckConfirm">
          <i class="el-icon-check"></i> 确认审核通过
        </el-button>
      </template>
    </el-dialog>

    <!-- 文件预览 -->
    <FilePreview :visible.sync="previewVisible" :url="previewFileUrl" />
  </div>
</template>

<script>
import {
  getRtReportModelApplyList,
  getRtReportModelApplyDetails,
  updateRtReportModelApply,
  checkRtReportModelApply,
  deleteRtReportModelApply,
  addRtReportModelApply,
  getRtReportModelApplyStatusGroupAll,
  normalizeApplyPageResponse,
  getRtReportModelListParam,
  getRtFxxkItineraryListAll,
  getRtFxxkItineraryDetails
} from '@/api/api'
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import FilePreview from '@/components/FilePreview.vue'

const STATUS_MAP = { 1: '待提交', 2: '待审核', 3: '已发包' }
const STATUS_GROUP_TABS = [
  { label: '全部申请', status: 0 },
  { label: '待提交', status: 1 },
  { label: '待审核', status: 2 },
  { label: '已发包', status: 3 }
]

// 字符串数组 → {label, value} 格式
const toOptions = arr => (arr || []).map(s => ({ label: s, value: s }))

// 行程表记录数组转销售订单号下拉选项（去重）
const toOrderNumOptions = arr => {
  const seen = new Set()
  return (arr || []).filter(item => {
    if (!item.rfiOrderNum || seen.has(item.rfiOrderNum)) return false
    seen.add(item.rfiOrderNum)
    return true
  }).map(item => ({ label: item.rfiProjectName, value: item.rfiOrderNum }))
}

function createEmptyFormData () {
  return { rrmaId: '', rfiOrderNum: '', rrmaCustomer: '', rrmaProvince: '', rrmaCity: '', rrmaCourseType: '', rrmaModelName: '', rrmaStatus: 1, applyRuleNameCustomer: '', applyRuleNameCustomerShort: '', applyRuleNameCourse: '', applyRuleNameDateStart: '', applyRuleNameDateEnd: '' }
}

// 新增表单空数据（多一个 applyuserid 字段）
function createEmptyAddFormData () {
  return { rfiOrderNum: '', applyuserid: '', rrmaCustomer: '', rrmaProvince: '', rrmaCity: '', rrmaCourseType: '', rrmaModelName: '', rrmaStatus: 1, applyRuleNameCustomer: '', applyRuleNameCustomerShort: '', applyRuleNameCourse: '', applyRuleNameDateStart: '', applyRuleNameDateEnd: '' }
}

export default {
  name: 'ReportApplyAdmin',
  components: { AjFormAdd, AjFormEdit, AjTable, EmptyState, FilePreview, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      statusMap: STATUS_MAP,
      statusColorMap: { 1: '#f59e0b', 2: '#4f7cff', 3: '#10b981' },

      // 行程表记录列表（页面初始化时加载一次）
      itineraryList: [],

      // 列表
      loading: false,
      statusGroupLoading: false,
      statusGroupRows: [],
      list: [],
      total: 0,
      query: { keySearch: '', rrmaCustomer: '', rrmaProvince: '', rrmaCity: '', rrmaCourseType: '', rrmaStatus: '', applyusername: '', checkusername: '', currentPage: 1, pageSize: 10 },

      // 搜索面板联动选项
      searchCustomerOptions: [],
      searchProvinceOptions: [],
      searchCityOptions: [],
      searchCourseTypeOptions: [],

      // 编辑弹窗
      submitLoading: false,
      editDialogVisible: false,
      editSubmitType: 1,
      editFormData: createEmptyFormData(),
      editOrderNumOptions: [],
      orderNumLoading: false,
      editCustomerOptions: [],
      editProvinceOptions: [],
      editCityOptions: [],
      editCourseTypeOptions: [],
      editModelNameOptions: [],

      // 新增弹窗
      addDialogVisible: false,
      addSubmitType: 1,
      addFormData: createEmptyAddFormData(),
      addOrderNumOptions: [],
      addMemberOptions: [],
      addCustomerOptions: [],
      addProvinceOptions: [],
      addCityOptions: [],
      addCourseTypeOptions: [],
      addModelNameOptions: [],

      // 审核相关（表单嵌入详情弹窗）
      checkForm: { rrmaStatusDes: '' },

      // 查看详情
      viewDialogVisible: false,
      viewData: null,

      // 文件预览
      previewVisible: false,
      previewFileUrl: ''
    }
  },
  computed: {
    statusTabs () {
      return STATUS_GROUP_TABS.map(item => {
        const queryStatus = this.query.rrmaStatus === '' ? 0 : Number(this.query.rrmaStatus)
        const matched = this.statusGroupRows.find(row => Number(row.rrmaStatus) === item.status)
        return {
          ...item,
          value: Number((matched && matched.statusNum) || 0),
          active: queryStatus === item.status
        }
      })
    },
    // 表格列配置
    columns () {
      return [
        { type: 'index', label: '序号', width: 60 },
        { prop: 'rfiOrderNum', label: '销售订单号', width: 140, showTooltip: true },
        { prop: 'rrmaCustomer', label: '客户群体', minWidth: 110 },
        { prop: 'rrmaProvince', label: '省份', width: 90 },
        { prop: 'rrmaCity', label: '地市', width: 90 },
        { prop: 'rrmaCourseType', label: '课程类型', width: 110 },
        { prop: 'rrmaModelName', label: '包内含资料', minWidth: 240 },
        { prop: 'applyusername', label: '申请人', width: 90 },
        {
          prop: 'rrmaStatus',
          label: '状态',
          width: 90,
          render: (h, { row }) => {
            const text = STATUS_MAP[row.rrmaStatus] || '未知'
            const colorMap = { 1: '#f59e0b', 2: '#4f7cff', 3: '#10b981' }
            return h('span', { style: { color: colorMap[row.rrmaStatus] || '#909399', fontWeight: '500' } }, text)
          }
        },
        { prop: 'applytime', label: '申请时间', width: 150 },
        { prop: 'checkusername', label: '审核人', width: 90 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 260,
          fixed: 'right',
          buttons: (row) => {
            const btns = [{ key: 'view', text: '查看', type: 'primary' }, { key: 'edit', text: '编辑', type: 'primary' }, { key: 'delete', text: '删除', type: 'danger' }]
            if (row.rrmaStatus === 2) {
              btns.push({ key: 'check', text: '审核', type: 'success', class: 'check-btn-highlight' })
            }

            return btns
          }
        }
      ]
    },
    // 编辑表单配置
    editFormConfig () {
      return [
        [
          { label: '销售订单号', prop: 'rfiOrderNum', type: 'select', span: 24, style: { width: '100%' }, filterable: true, remote: true, remoteMethod: this.remoteSearchOrderNum, options: this.editOrderNumOptions, clearable: true, placeholder: '输入行程名称搜索订单号' }
        ],
        [
          { label: '客户群体', prop: 'rrmaCustomer', type: 'select', span: 12, required: true, options: this.editCustomerOptions, clearable: true },
          { label: '省份', prop: 'rrmaProvince', type: 'select', span: 12, required: true, options: this.editProvinceOptions, clearable: true, disabled: !this.editFormData.rrmaCustomer }
        ],
        [
          { label: '地市', prop: 'rrmaCity', type: 'select', span: 12, options: this.editCityOptions, clearable: true, disabled: !this.editFormData.rrmaProvince },
          { label: '课程类型', prop: 'rrmaCourseType', type: 'select', span: 12, required: true, options: this.editCourseTypeOptions, clearable: true, disabled: !this.editFormData.rrmaCity }
        ],
        [
          { label: '包内含资料', prop: 'rrmaModelName', style: { width: '95%' }, type: 'select', span: 24, required: true, options: this.editModelNameOptions, clearable: true, disabled: !this.editFormData.rrmaCourseType }
        ],
        [
          { label: '客户名称', prop: 'applyRuleNameCustomer', type: 'input', span: 12 },
          { label: '客户简称', prop: 'applyRuleNameCustomerShort', type: 'input', span: 12 }
        ],
        [
          { label: '课程名称', prop: 'applyRuleNameCourse', type: 'input', span: 24 }
        ],
        [
          { label: '开始日期', prop: 'applyRuleNameDateStart', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 },
          { label: '结束日期', prop: 'applyRuleNameDateEnd', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 }
        ]
      ]
    },
    // 新增表单配置（多了分配用户字段）
    addFormConfig () {
      return [
        [
          { label: '销售订单号', prop: 'rfiOrderNum', type: 'select', span: 24, style: { width: '100%' }, filterable: true, remote: true, remoteMethod: this.remoteSearchOrderNum, options: this.addOrderNumOptions, clearable: true, placeholder: '输入行程名称搜索订单号' }
        ],
        [
          { label: '分配用户', prop: 'applyuserid', type: 'select', span: 24, required: true, filterable: true, options: this.addMemberOptions, clearable: true, disabled: !this.addFormData.rfiOrderNum, placeholder: '请先选择销售订单号' }
        ],
        [
          { label: '客户群体', prop: 'rrmaCustomer', type: 'select', span: 12, required: true, options: this.addCustomerOptions, clearable: true },
          { label: '省份', prop: 'rrmaProvince', type: 'select', span: 12, required: true, options: this.addProvinceOptions, clearable: true, disabled: !this.addFormData.rrmaCustomer }
        ],
        [
          { label: '地市', prop: 'rrmaCity', type: 'select', span: 12, options: this.addCityOptions, clearable: true, disabled: !this.addFormData.rrmaProvince },
          { label: '课程类型', prop: 'rrmaCourseType', type: 'select', span: 12, required: true, options: this.addCourseTypeOptions, clearable: true, disabled: !this.addFormData.rrmaCity }
        ],
        [
          { label: '包内含资料', prop: 'rrmaModelName', style: { width: '95%' }, type: 'select', span: 24, required: true, options: this.addModelNameOptions, clearable: true, disabled: !this.addFormData.rrmaCourseType }
        ],
        [
          { label: '客户名称', prop: 'applyRuleNameCustomer', type: 'input', span: 12 },
          { label: '客户简称', prop: 'applyRuleNameCustomerShort', type: 'input', span: 12 }
        ],
        [
          { label: '课程名称', prop: 'applyRuleNameCourse', type: 'input', span: 24 }
        ],
        [
          { label: '开始日期', prop: 'applyRuleNameDateStart', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 },
          { label: '结束日期', prop: 'applyRuleNameDateEnd', type: 'date', valueFormat: 'yyyy-MM-dd', span: 12 }
        ]
      ]
    }
  },
  created () {
    this.fetchList()
    this.loadStatusGroup()
    this.loadSearchCustomerOptions()
    this.remoteSearchOrderNum('')
  },
  methods: {
    // ============ 远程搜索订单号（管理员端，空关键词加载默认列表） ============
    async remoteSearchOrderNum (query) {
      this.orderNumLoading = true
      try {
        const params = {}
        if (query && query.trim()) params.afiProjectName = query.trim()
        const res = await getRtFxxkItineraryListAll(params)
        this.itineraryList = res.data || []
        const options = toOrderNumOptions(res.data)
        this.editOrderNumOptions = options
        this.addOrderNumOptions = options
      } catch (e) {
        this.itineraryList = []
      } finally {
        this.orderNumLoading = false
      }
    },

    // ============ 列表查询 ============
    async fetchList () {
      this.loading = true
      try {
        const params = {}
        if (this.query.keySearch) params.keySearch = this.query.keySearch
        if (this.query.rrmaCustomer) params.rrmaCustomer = this.query.rrmaCustomer
        if (this.query.rrmaProvince) params.rrmaProvince = this.query.rrmaProvince
        if (this.query.rrmaCity) params.rrmaCity = this.query.rrmaCity
        if (this.query.rrmaCourseType) params.rrmaCourseType = this.query.rrmaCourseType
        if (this.query.rrmaStatus) params.rrmaStatus = this.query.rrmaStatus
        if (this.query.applyusername) params.applyusername = this.query.applyusername
        if (this.query.checkusername) params.checkusername = this.query.checkusername
        params.currentPage = this.query.currentPage
        params.pageSize = this.query.pageSize
        const res = await getRtReportModelApplyList(params)
        const pageData = normalizeApplyPageResponse(res)
        this.list = pageData.list
        this.total = pageData.total
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadStatusGroup () {
      this.statusGroupLoading = true
      try {
        const res = await getRtReportModelApplyStatusGroupAll({})
        this.statusGroupRows = Array.isArray(res && res.data) ? res.data : []
      } catch (error) {
        this.statusGroupRows = []
      } finally {
        this.statusGroupLoading = false
      }
    },
    handleStatusTabClick (item) {
      const nextStatus = item.status === 0 ? '' : item.status
      if (this.query.rrmaStatus === nextStatus) return
      this.query.rrmaStatus = nextStatus
      this.handleSearch()
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { keySearch: '', rrmaCustomer: '', rrmaProvince: '', rrmaCity: '', rrmaCourseType: '', rrmaStatus: '', applyusername: '', checkusername: '', currentPage: 1, pageSize: 10 }
      this.searchProvinceOptions = []
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      this.fetchList()
      this.loadStatusGroup()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },

    // ============ 搜索面板联动 ============
    async loadSearchCustomerOptions () {
      try {
        const res = await getRtReportModelListParam({ modelType: 1 })
        this.searchCustomerOptions = res.data || []
      } catch (e) {
        this.searchCustomerOptions = []
      }
    },
    async handleSearchCustomerChange (val) {
      this.query.rrmaProvince = ''
      this.query.rrmaCity = ''
      this.query.rrmaCourseType = ''
      this.searchProvinceOptions = []
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParam({ modelType: 2, rrmCustomer: val })
        this.searchProvinceOptions = res.data || []
      } catch (e) {}
    },
    async handleSearchProvinceChange (val) {
      this.query.rrmaCity = ''
      this.query.rrmaCourseType = ''
      this.searchCityOptions = []
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParam({ modelType: 3, rrmCustomer: this.query.rrmaCustomer, rrmProvince: val })
        this.searchCityOptions = res.data || []
      } catch (e) {}
    },
    async handleSearchCityChange (val) {
      this.query.rrmaCourseType = ''
      this.searchCourseTypeOptions = []
      if (!val) return
      try {
        const res = await getRtReportModelListParam({ modelType: 4, rrmCustomer: this.query.rrmaCustomer, rrmProvince: this.query.rrmaProvince, rrmCity: val })
        this.searchCourseTypeOptions = res.data || []
      } catch (e) {}
    },

    // ============ 编辑表单联动 ============
    async loadFormOptions (modelType, rrmCustomer, rrmProvince, rrmCity, rrmCourseType) {
      const params = { modelType }
      if (rrmCustomer) params.rrmCustomer = rrmCustomer
      if (rrmProvince) params.rrmProvince = rrmProvince
      if (rrmCity) params.rrmCity = rrmCity
      if (rrmCourseType) params.rrmCourseType = rrmCourseType
      try {
        const res = await getRtReportModelListParam(params)
        return toOptions(res.data)
      } catch (e) {
        return []
      }
    },
    // 级联自动选中：选项只有一项时自动选中并继续加载下一级
    async autoCascade (currentOptions, currentModelType, mode = 'edit') {
      if (!currentOptions || currentOptions.length !== 1 || currentModelType > 5) return
      const value = currentOptions[0].value
      const formData = mode === 'add' ? this.addFormData : this.editFormData
      const propMap = { 1: 'rrmaCustomer', 2: 'rrmaProvince', 3: 'rrmaCity', 4: 'rrmaCourseType', 5: 'rrmaModelName' }
      const formDataProp = propMap[currentModelType]
      if (!formDataProp) return
      formData[formDataProp] = value
      if (currentModelType >= 5) return
      for (let i = currentModelType + 1; i <= 5; i++) {
        formData[propMap[i]] = ''
      }
      const nextModelType = currentModelType + 1
      const nextOpts = await this.loadFormOptions(
        nextModelType,
        formData.rrmaCustomer,
        formData.rrmaProvince,
        formData.rrmaCity,
        formData.rrmaCourseType
      )
      const optionsMap = mode === 'add'
        ? { 2: 'addProvinceOptions', 3: 'addCityOptions', 4: 'addCourseTypeOptions', 5: 'addModelNameOptions' }
        : { 2: 'editProvinceOptions', 3: 'editCityOptions', 4: 'editCourseTypeOptions', 5: 'editModelNameOptions' }
      this[optionsMap[nextModelType]] = nextOpts
      for (let i = nextModelType + 1; i <= 5; i++) {
        this[optionsMap[i]] = []
      }
      await this.autoCascade(nextOpts, nextModelType, mode)
    },
    async handleFormItemChange (mode, { prop, value }) {
      const formData = mode === 'add' ? this.addFormData : this.editFormData
      const optionsMap = mode === 'add'
        ? { 2: 'addProvinceOptions', 3: 'addCityOptions', 4: 'addCourseTypeOptions', 5: 'addModelNameOptions' }
        : { 2: 'editProvinceOptions', 3: 'editCityOptions', 4: 'editCourseTypeOptions', 5: 'editModelNameOptions' }
      if (prop === 'rfiOrderNum') {
        // 清空订单号时重置关联字段并重新加载默认列表
        if (!value) {
          formData.applyRuleNameCustomer = ''
          formData.applyRuleNameCustomerShort = ''
          formData.applyRuleNameCourse = ''
          formData.applyRuleNameDateStart = ''
          formData.applyRuleNameDateEnd = ''
          if (mode === 'add') {
            formData.applyuserid = ''
            this.addMemberOptions = []
          }
          this.remoteSearchOrderNum('')
          return
        }
        // 选择销售订单号后，自动填充关联字段
        const selected = (this.itineraryList || []).find(item => item.rfiOrderNum === value)
        if (selected) {
          formData.applyRuleNameCustomer = selected.rfiProjectCustomer || ''
          formData.applyRuleNameCustomerShort = selected.rfiProjectCustomerShort || ''
          formData.applyRuleNameCourse = selected.rfiCourseName || ''
          formData.applyRuleNameDateStart = selected.rfiCourseDateStart || ''
          formData.applyRuleNameDateEnd = selected.rfiCourseDateEnd || ''
        } else {
          formData.applyRuleNameCustomer = ''
          formData.applyRuleNameCustomerShort = ''
          formData.applyRuleNameCourse = ''
          formData.applyRuleNameDateStart = ''
          formData.applyRuleNameDateEnd = ''
        }
        // 新增模式下，加载行程成员列表供分配用户选择
        if (mode === 'add') {
          formData.applyuserid = ''
          this.addMemberOptions = []
          if (selected && selected.rfiId) {
            try {
              const detailRes = await getRtFxxkItineraryDetails({ rfiId: selected.rfiId })
              const members = (detailRes.data && detailRes.data.rtFxxkItineraryMembersList) || []
              this.addMemberOptions = members.filter(m => m.auId).map(m => ({ label: m.rfimMemberName, value: m.auId }))
            } catch (e) {
              this.addMemberOptions = []
            }
          }
        }
      } else if (prop === 'rrmaCustomer') {
        formData.rrmaProvince = ''
        formData.rrmaCity = ''
        formData.rrmaCourseType = ''
        formData.rrmaModelName = ''
        this[optionsMap[2]] = await this.loadFormOptions(2, value, '', '')
        this[optionsMap[3]] = []
        this[optionsMap[4]] = []
        this[optionsMap[5]] = []
        await this.autoCascade(this[optionsMap[2]], 2, mode)
      } else if (prop === 'rrmaProvince') {
        formData.rrmaCity = ''
        formData.rrmaCourseType = ''
        formData.rrmaModelName = ''
        this[optionsMap[3]] = await this.loadFormOptions(3, formData.rrmaCustomer, value, '')
        this[optionsMap[4]] = []
        this[optionsMap[5]] = []
        await this.autoCascade(this[optionsMap[3]], 3, mode)
      } else if (prop === 'rrmaCity') {
        formData.rrmaCourseType = ''
        formData.rrmaModelName = ''
        this[optionsMap[4]] = await this.loadFormOptions(4, formData.rrmaCustomer, formData.rrmaProvince, value)
        this[optionsMap[5]] = []
        await this.autoCascade(this[optionsMap[4]], 4, mode)
      } else if (prop === 'rrmaCourseType') {
        formData.rrmaModelName = ''
        this[optionsMap[5]] = await this.loadFormOptions(5, formData.rrmaCustomer, formData.rrmaProvince, formData.rrmaCity, value)
        await this.autoCascade(this[optionsMap[5]], 5, mode)
      } else if (prop === 'rrmaStatus') {
        if (mode === 'add') {
          this.addSubmitType = value || 1
        } else {
          this.editSubmitType = value || 1
        }
      }
    },

    // ============ 新增 ============
    async handleAdd () {
      this.addFormData = createEmptyAddFormData()
      this.addSubmitType = 1
      this.addMemberOptions = []
      this.addCustomerOptions = []
      this.addProvinceOptions = []
      this.addCityOptions = []
      this.addCourseTypeOptions = []
      this.addModelNameOptions = []
      // 新增时订单号为空，加载默认行程列表供下拉选择
      this.remoteSearchOrderNum('')
      // 加载客户群体选项（第一级级联）
      try {
        const res = await getRtReportModelListParam({ modelType: 1 })
        this.addCustomerOptions = toOptions(res.data)
      } catch (e) {
        this.addCustomerOptions = []
      }
      this.addDialogVisible = true
    },
    async handleAddConfirm ({ data }) {
      this.submitLoading = true
      try {
        const submitType = this.addSubmitType || data.rrmaStatus || 1
        await addRtReportModelApply({
          applyuserid: data.applyuserid,
          rfiOrderNum: data.rfiOrderNum,
          rrmaCustomer: data.rrmaCustomer,
          rrmaProvince: data.rrmaProvince,
          rrmaCity: data.rrmaCity,
          rrmaCourseType: data.rrmaCourseType,
          rrmaModelName: data.rrmaModelName,
          rrmaStatus: submitType,
          applyRuleNameCustomer: data.applyRuleNameCustomer,
          applyRuleNameCustomerShort: data.applyRuleNameCustomerShort,
          applyRuleNameCourse: data.applyRuleNameCourse,
          applyRuleNameDateStart: data.applyRuleNameDateStart,
          applyRuleNameDateEnd: data.applyRuleNameDateEnd
        })
        this.$message.success(submitType === 1 ? '申请已保存' : '申请已提交')
        this.addDialogVisible = false
        this.fetchList()
        this.loadStatusGroup()
      } catch (e) {
        this.$message.error(e.message || '新增申请失败')
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 编辑 ============
    async handleEdit (row) {
      try {
        this.editSubmitType = row.rrmaStatus || 1
        const res = await getRtReportModelApplyDetails({ rrmaId: row.rrmaId })
        const detail = res.data || row
        this.editSubmitType = detail.rrmaStatus || 1
        this.editFormData = {
          rrmaId: detail.rrmaId,
          rfiOrderNum: detail.rfiOrderNum || '',
          rrmaCustomer: detail.rrmaCustomer || '',
          rrmaProvince: detail.rrmaProvince || '',
          rrmaCity: detail.rrmaCity || '',
          rrmaCourseType: detail.rrmaCourseType || '',
          rrmaModelName: detail.rrmaModelName || '',
          rrmaStatus: detail.rrmaStatus || 1,
          applyRuleNameCustomer: detail.applyRuleNameCustomer || '',
          applyRuleNameCustomerShort: detail.applyRuleNameCustomerShort || '',
          applyRuleNameCourse: detail.applyRuleNameCourse || '',
          applyRuleNameDateStart: detail.applyRuleNameDateStart || '',
          applyRuleNameDateEnd: detail.applyRuleNameDateEnd || ''
        }
        // 逐级加载联动选项
        const customerRes = await getRtReportModelListParam({ modelType: 1 })
        this.editCustomerOptions = toOptions(customerRes.data)
        if (detail.rrmaCustomer) {
          const provinceRes = await getRtReportModelListParam({ modelType: 2, rrmCustomer: detail.rrmaCustomer })
          this.editProvinceOptions = toOptions(provinceRes.data)
        } else {
          this.editProvinceOptions = []
        }
        if (detail.rrmaCustomer && detail.rrmaProvince) {
          const cityRes = await getRtReportModelListParam({ modelType: 3, rrmCustomer: detail.rrmaCustomer, rrmProvince: detail.rrmaProvince })
          this.editCityOptions = toOptions(cityRes.data)
        } else {
          this.editCityOptions = []
        }
        if (detail.rrmaCustomer && detail.rrmaProvince && detail.rrmaCity) {
          const typeRes = await getRtReportModelListParam({ modelType: 4, rrmCustomer: detail.rrmaCustomer, rrmProvince: detail.rrmaProvince, rrmCity: detail.rrmaCity })
          this.editCourseTypeOptions = toOptions(typeRes.data)
        } else {
          this.editCourseTypeOptions = []
        }
        if (detail.rrmaCustomer && detail.rrmaProvince && detail.rrmaCity && detail.rrmaCourseType) {
          const modelRes = await getRtReportModelListParam({ modelType: 5, rrmCustomer: detail.rrmaCustomer, rrmProvince: detail.rrmaProvince, rrmCity: detail.rrmaCity, rrmCourseType: detail.rrmaCourseType })
          this.editModelNameOptions = toOptions(modelRes.data)
        } else {
          this.editModelNameOptions = []
        }
        // 编辑时也加载默认行程列表，方便用户更换订单号
        this.remoteSearchOrderNum('')
        this.editDialogVisible = true
      } catch (error) {
        this.$message.error('获取申请详情失败')
      }
    },
    async handleEditSubmit ({ data }) {
      this.submitLoading = true
      try {
        const submitType = this.editSubmitType || data.rrmaStatus || 1
        await updateRtReportModelApply({
          rrmaId: data.rrmaId,
          rfiOrderNum: data.rfiOrderNum,
          rrmaCustomer: data.rrmaCustomer,
          rrmaProvince: data.rrmaProvince,
          rrmaCity: data.rrmaCity,
          rrmaCourseType: data.rrmaCourseType,
          rrmaModelName: data.rrmaModelName,
          rrmaStatus: submitType,
          applyRuleNameCustomer: data.applyRuleNameCustomer,
          applyRuleNameCustomerShort: data.applyRuleNameCustomerShort,
          applyRuleNameCourse: data.applyRuleNameCourse,
          applyRuleNameDateStart: data.applyRuleNameDateStart,
          applyRuleNameDateEnd: data.applyRuleNameDateEnd
        })
        this.$message.success(submitType === 1 ? '申请已保存' : '申请已提交')
        this.editDialogVisible = false
        this.fetchList()
        this.loadStatusGroup()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 审核（打开详情弹窗） ============
    handleCheck (row) {
      this.checkForm = { rrmaStatusDes: '' }
      this.handleView(row)
    },
    async handleCheckConfirm () {
      if (!this.viewData) return
      this.submitLoading = true
      try {
        await checkRtReportModelApply({
          rrmaId: this.viewData.rrmaId,
          rrmaStatusDes: this.checkForm.rrmaStatusDes
        })
        this.$message.success('审核通过，已发包')
        this.viewDialogVisible = false
        this.fetchList()
        this.loadStatusGroup()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 删除 ============
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该申请记录？', '提示', { type: 'warning' })
        await deleteRtReportModelApply({ rrmaId: row.rrmaId })
        this.$message.success('申请记录已删除')
        this.fetchList()
        this.loadStatusGroup()
      } catch (error) {}
    },

    // ============ 查看详情 ============
    async handleView (row) {
      try {
        const res = await getRtReportModelApplyDetails({ rrmaId: row.rrmaId })
        this.viewData = res.data || row
        this.viewDialogVisible = true
      } catch (error) {
        this.$message.error('获取申请详情失败')
      }
    },

    // ============ 操作按钮分发 ============
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'view': this.handleView(row); break
        case 'check': this.handleCheck(row); break
        case 'edit': this.handleEdit(row); break
        case 'delete': this.handleDelete(row); break
      }
    },

    // ============ 文件预览与下载 ============
    openFilePreview (url) {
      this.previewFileUrl = url
      this.previewVisible = true
    },
    // 下载资料文件到电脑（<a> 标签下载，不走 fetch，避免跨域）
    downloadToLocal (url) {
      if (!url) return
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      this.$message({ type: 'success', message: '文件已开始下载，可在浏览器下载栏（Ctrl+J）查看', duration: 5000 })
    }
  }
}
</script>

<style scoped>
/* 操作列按钮间距 */
::v-deep .aj-table-operation-link {
  margin-right: 10px;
}
::v-deep .aj-table-operation-link:last-child {
  margin-right: 0;
}

/* 审核按钮 — 醒目翡翠绿实底，一眼可见 */
::v-deep .el-table .aj-table-operation-link.check-btn-highlight.el-link {
  color: #fff !important;
  background: linear-gradient(135deg, #10b981, #059669) !important;
  border-color: transparent !important;
  box-shadow: 0 3px 10px rgba(16, 185, 129, 0.45) !important;
  font-weight: 900 !important;
}
::v-deep .el-table .aj-table-operation-link.check-btn-highlight.el-link:hover {
  color: #fff !important;
  background: linear-gradient(135deg, #34d399, #10b981) !important;
  box-shadow: 0 5px 16px rgba(16, 185, 129, 0.55) !important;
  transform: translateY(-1px) !important;
}

/* 审核操作悬浮栏（固定在弹窗底部） */
.detail-check-bar {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 20px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}
.detail-check-bar__label {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  line-height: 32px;
  white-space: nowrap;
}
.detail-check-bar__label i {
  color: #4f7cff;
  margin-right: 4px;
}
.detail-check-bar__input {
  flex: 1;
}

/* 查看详情弹窗 */
::v-deep .detail-dialog .el-dialog__body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 !important;
}

/* 弹窗 body 内部 flex 容器 */
.detail-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* 可滚动的内容区域 */
.detail-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 12px;
}

.detail-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 32px;
}
.detail-dialog-title {
  font-size: 17px;
  font-weight: 600;
  color: #1e293b;
}

.detail-scroll .detail-content { padding: 0 4px; }

.detail-status-bar {
  text-align: center;
  margin-bottom: 18px;
}
.detail-status-tag {
  display: inline-block;
  padding: 4px 18px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
}
.detail-status--1 { background: #fef3c7; color: #d97706; }
.detail-status--2 { background: #dbeafe; color: #2563eb; }
.detail-status--3 { background: #d1fae5; color: #059669; }

.detail-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  margin: 18px 0 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}
.detail-section-title i { color: #4f7cff; font-size: 16px; }

.detail-table {
  display: flex;
  flex-direction: column;
  gap: 0;
  font-size: 14px;
}
.detail-table--2col .detail-row {
  display: grid;
  grid-template-columns: 100px 1fr 100px 1fr;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #f1f5f9;
}
.detail-cell {
  padding: 10px 12px;
}
.detail-cell--label {
  width: 90px;
  flex-shrink: 0;
  color: #64748b;
  background: #f8fafc;
  font-size: 13px;
}
.detail-cell--value {
  flex: 1;
  color: #1e293b;
  word-break: break-all;
}
.detail-cell--pre {
  white-space: pre-wrap;
}

.detail-file-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.detail-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #eff6ff;
  border-radius: 4px;
  font-size: 13px;
  color: #4f7cff;
  text-decoration: none;
  transition: background 0.2s;
}
.detail-file-chip:hover { background: #dbeafe; }

/* 文件预览入口 */
.detail-file-preview {
  cursor: pointer;
  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.15);
  }
}
.detail-download-btn {
  color: #10b981;
  padding: 5px 8px;
  &:hover { color: #059669; }
}

.template-des {
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;
}
.template-img-wrap {
  margin-top: 8px;
}
</style>
